import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Popover } from '@heroui/react/popover';
import { ArrowLeft, ArrowRight, LayoutGrid } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  dashboardRoutePaths,
  settingsSectionPath,
} from '@/routes/route-config';
import { groupSections } from '@/pages/settings/sections';
import type { Permission } from '@/types/auth.types';
import {
  NavLinkRow,
  NavGroupRow,
  NavSectionLabel,
  NavSectionRule,
  NavSubheadingRow,
} from './NavItem';
import { useLabel } from './use-label';
import {
  useActiveNav,
  useVisibleNav,
  useVisibleSettings,
} from './use-nav-state';
import type { ClinicRole, NavCounter, NavGroup, NavLeaf } from './nav-config';
import type { useOpenNavGroups } from './use-shell-state';

/**
 * The navigation tree. Rendered identically by the desktop rail and
 * the mobile drawer — the ONLY difference is the `collapsed` flag and
 * the `onNavigate` callback the drawer passes to close itself.
 *
 * Previously these were two separate JSX trees that had already
 * drifted (the mobile bottom nav pointed at routes the sidebar had
 * since regrouped).
 */

/**
 * Where "Back" in settings returns to: the last page outside settings. Module
 * state rather than storage — it only has to outlive the route change, and a
 * fresh tab that opens straight onto settings has nowhere better than home.
 */
let lastAppPath: string = dashboardRoutePaths.home;

export interface SidebarNavProps {
  collapsed: boolean;
  permissions: Permission[] | undefined;
  role?: ClinicRole;
  /** Live counters resolved by the shell and injected by name. */
  counts: Record<NavCounter, number>;
  groups: ReturnType<typeof useOpenNavGroups>;
  onNavigate?: () => void;
}

export function SidebarNav({
  collapsed,
  permissions,
  role,
  counts,
  groups,
  onNavigate,
}: SidebarNavProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const sections = useVisibleNav(permissions, role);
  const settingsSections = useVisibleSettings(permissions);
  const {
    isLeafActive,
    isGroupActive,
    isSettingsActive,
    inSettings,
    onSettingsHome,
  } = useActiveNav();
  const { isRTL } = useLanguage();
  const { pathname, search } = useLocation();
  const { isOpen, toggle, ensureOpen } = groups;

  useEffect(() => {
    if (!inSettings) lastAppPath = pathname + search;
  }, [inSettings, pathname, search]);

  // A deep link to a section low in the list (Currency, Staff) would land
  // with its highlighted row below the fold of the rail. Keyed on the list as
  // well: permission-gated rows only exist once permissions arrive.
  const settingsNavRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const row = settingsNavRef.current?.querySelector('[aria-current="page"]');
    const viewport = settingsNavRef.current?.parentElement;
    if (!row || !viewport) return;
    const r = row.getBoundingClientRect();
    const v = viewport.getBoundingClientRect();
    // Only when it is off screen: re-centring a row the user just clicked
    // would yank the list out from under the pointer.
    if (r.top < v.top || r.bottom > v.bottom) {
      row.scrollIntoView({ block: 'center' });
    }
  }, [pathname, settingsSections]);

  // Landing inside a group opens it. Additive only, so a group the
  // user deliberately closed stays closed while they browse it.
  const activeGroupId = sections
    .flatMap((s) => s.items)
    .find((i): i is NavGroup => i.kind === 'group' && isGroupActive(i))?.id;

  useEffect(() => {
    if (activeGroupId) ensureOpen(activeGroupId);
  }, [activeGroupId, ensureOpen]);

  const badgeFor = (leaf: NavLeaf) => (leaf.badge ? counts[leaf.badge] : 0);

  /**
   * `isCollapsed` is an explicit override rather than the ambient
   * `collapsed`: rows inside a flyout are always fully expanded, even
   * though the rail behind them is collapsed. Without it the flyout
   * rendered a column of bare icons, each firing its own tooltip.
   */
  const renderLeaf = (leaf: NavLeaf, depth: 0 | 1, isCollapsed = collapsed) => (
    <NavLinkRow
      key={leaf.id}
      to={leaf.href}
      icon={leaf.icon}
      label={label(leaf.labelKey, leaf.labelFallback)}
      isActive={isLeafActive(leaf)}
      collapsed={isCollapsed}
      badge={badgeFor(leaf)}
      badgeTone={leaf.badge === 'pending' ? 'accent' : 'primary'}
      depth={depth}
      locked={leaf.locked}
      onNavigate={onNavigate}
    />
  );

  const renderGroupChildren = (group: NavGroup, isCollapsed = collapsed) =>
    group.children
      .filter((c) => !(c.kind === 'link' && c.hideWhenZero && badgeFor(c) <= 0))
      .map((child, i) =>
        child.kind === 'subheading' ? (
          <NavSubheadingRow key={`h-${i}`}>
            {label(child.labelKey, child.labelFallback)}
          </NavSubheadingRow>
        ) : (
          renderLeaf(child, 1, isCollapsed)
        ),
      );

  const renderGroup = (group: NavGroup) => {
    const open = isOpen(group.id) && !collapsed;
    const panelId = `nav-group-${group.id}`;
    const groupLabel = label(group.labelKey, group.labelFallback);

    /*
      On the icon rail there is nowhere to expand INTO, so a group has
      to open as a flyout instead. Without this, clicking Finance (or
      any group) while collapsed toggled hidden state and appeared to
      do nothing at all — a dead end on every grouped destination.

      Popover.Root is a DialogTrigger underneath, so the flyout gets
      focus containment, Escape, and click-away for free.
    */
    if (collapsed) {
      return (
        <Popover.Root key={group.id}>
          <Popover.Trigger className="block w-full">
            <NavGroupRow
              icon={group.icon}
              label={groupLabel}
              isActive={isGroupActive(group)}
              isOpen={false}
              collapsed
              badge={group.badge ? counts[group.badge] : 0}
              locked={group.locked}
              controls={panelId}
              onToggle={() => {}}
            />
          </Popover.Trigger>
          <Popover.Content
            placement="end top"
            offset={8}
            className="w-64 p-1.5"
          >
            <p className="px-2.5 pb-1.5 pt-1 text-micro font-semibold uppercase tracking-wider text-secondary-400">
              {groupLabel}
            </p>
            <div className="max-h-[min(70vh,32rem)] space-y-0.5 overflow-y-auto scroll-thin">
              {renderGroupChildren(group, false)}
            </div>
          </Popover.Content>
        </Popover.Root>
      );
    }

    return (
      <div key={group.id}>
        <NavGroupRow
          icon={group.icon}
          label={groupLabel}
          isActive={isGroupActive(group)}
          isOpen={open}
          collapsed={false}
          badge={group.badge ? counts[group.badge] : 0}
          locked={group.locked}
          controls={panelId}
          onToggle={() => toggle(group.id)}
        />
        {open && (
          <div
            id={panelId}
            className={cn(
              'mt-0.5 mb-1 space-y-0.5 border-border-default',
              'ms-4 border-s ps-2.5',
            )}
          >
            {renderGroupChildren(group)}
          </div>
        )}
      </div>
    );
  };

  /*
    Settings is its own place, not one more group in the rail. Inside it the
    rail swaps to the settings sections, grouped under headings, with a way
    back out at the top — 22 links nested under a single accordion row were
    a wall, and the clinic's work nav is no use while configuring it.
  */
  if (inSettings) {
    return (
      <nav
        ref={settingsNavRef}
        aria-label={t('nav.settings')}
        className="px-2 pb-2 pt-2"
      >
        <div className="space-y-0.5">
          <NavLinkRow
            to={lastAppPath}
            icon={isRTL ? ArrowRight : ArrowLeft}
            label={t('settings.nav.backToApp')}
            isActive={false}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
          <NavLinkRow
            to={dashboardRoutePaths.settings}
            icon={LayoutGrid}
            label={t('settings.nav.overview')}
            isActive={onSettingsHome}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        </div>
        {groupSections(settingsSections).map((group) => (
          <div key={group.id} className="pt-2">
            {collapsed ? (
              <NavSectionRule />
            ) : (
              <NavSectionLabel>{t(group.labelKey)}</NavSectionLabel>
            )}
            <div className="space-y-0.5">
              {group.sections.map((spec) => (
                <NavLinkRow
                  key={spec.id}
                  to={settingsSectionPath(spec.id)}
                  icon={spec.icon}
                  label={t(spec.labelKey)}
                  isActive={isSettingsActive(spec.id)}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>
    );
  }

  return (
    <nav aria-label={t('nav.primary', 'Primary')} className="px-2 pb-2">
      {sections.map((section) => (
        <div key={section.id}>
          {collapsed ? (
            <NavSectionRule />
          ) : (
            <NavSectionLabel>
              {label(section.labelKey, section.labelFallback)}
            </NavSectionLabel>
          )}
          <div className="space-y-0.5">
            {section.items.map((item) =>
              item.kind === 'group' ? renderGroup(item) : renderLeaf(item, 0),
            )}
          </div>
        </div>
      ))}
    </nav>
  );
}
